import { Thinking } from "@/components/ai/chat/Thinking";
import type { AgentMessage, AgentMessageFragment, Message, UserMessage } from "@/hooks/use-stream-conversation";
import { useCallback, type FC } from "react";
import { UserAvatar } from "@/components/ai/chat/UserAvatar";
import { Markdown } from "@/components/ai/chat/Markdown";
import { FragmentFileModification } from "@/components/ai/FragmentFileModification";
import { FragmentAction } from "@/components/ai/FragmentAction";
import { FragmentText } from "@/components/ai/FragmentText";
import { FragmentEnvironmentCreation } from "@/components/ai/FragmentEnvironmentCreation";
import { FragmentHostAuthentication } from "@/components/ai/FragmentHostAuthentication";
import { IconCopy } from "@/assets/icons/geist/IconCopy";
import { IconThumbsDown } from "@/assets/icons/geist/IconThumbsDown";
import { IconThumbsUp } from "@/assets/icons/geist/IconThumbsUp";
import { Button } from "@/components/flexkit/Button";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import { IconCheck } from "@/assets/icons/geist/IconCheck";
import { toast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { messageToText } from "@/utils/agent-executions";
import { useSegmentTrack } from "@/hooks/use-segment";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { AgentExecution_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";
import { Tooltip } from "@/components/Tooltip";
import { cn } from "@/components/podkit/lib/cn";
import { FragmentPlan } from "@/components/ai/FragmentPlan";

export const ChatBubble: FC<{
    message: Message;
    onClick?: (message: AgentMessageFragment) => void;
    agentExecution: PlainAgentExecution;
    isLast: boolean;
}> = ({ message, onClick, agentExecution, isLast }) => {
    return (
        <div
            className={cn("flex w-full flex-col gap-2 p-4 text-base", {
                "p-0": message.type === "agent",
                "rounded-xl border-[0.5px] border-border-base bg-[rgba(227,247,247,0.2)]": message.type === "user",
            })}
        >
            {message.type === "agent" && (
                <ChatBubbleAgent message={message} onClick={onClick} agentExecution={agentExecution} isLast={isLast} />
            )}
            {message.type === "user" && <ChatBubbleUser message={message} />}
        </div>
    );
};

const ChatBubbleAgent: FC<{
    message: AgentMessage;
    onClick?: (message: AgentMessageFragment) => void;
    agentExecution: PlainAgentExecution;
    isLast: boolean;
}> = ({ message, onClick, agentExecution, isLast }) => {
    const segmentTrack = useSegmentTrack();
    const [copied, setCopied] = useTemporaryState(false, 2000);
    const [votedUp, setVotedUp] = useTemporaryState(false, 2000);
    const [votedDown, setVotedDown] = useTemporaryState(false, 2000);

    const firstBlockId = message?.fragments?.[0]?.id;
    const lastBlockId = message?.fragments?.[message?.fragments?.length - 1]?.id;

    const handleCopyToClipboard = useCallback(async () => {
        setCopied(true);
        try {
            await navigator.clipboard.writeText(messageToText(message));
        } catch (error) {
            setCopied(false);
            toast({
                title: "Failed to copy to clipboard",
                description: formatError(error),
            });
        }
    }, [setCopied, message]);

    const handleThumbsUp = useCallback(() => {
        setVotedUp(true);
        segmentTrack("agent_message_voted", {
            vote: "up",
            agent_id: agentExecution?.spec?.agentId,
            agent_execution_id: agentExecution.id,
            first_block_id: firstBlockId,
            last_block_id: lastBlockId,
        });
    }, [setVotedUp, segmentTrack, agentExecution?.spec?.agentId, agentExecution.id, firstBlockId, lastBlockId]);

    const handleThumbsDown = useCallback(() => {
        setVotedDown(true);
        segmentTrack("agent_message_voted", {
            vote: "down",
            agent_id: agentExecution?.spec?.agentId,
            agent_execution_id: agentExecution.id,
            first_block_id: firstBlockId,
            last_block_id: lastBlockId,
        });
    }, [setVotedDown, segmentTrack, agentExecution?.spec?.agentId, agentExecution.id, firstBlockId, lastBlockId]);

    const isDone =
        agentExecution.status?.phase === AgentExecution_Phase.WAITING_FOR_INPUT ||
        agentExecution.status?.phase === AgentExecution_Phase.STOPPED;

    const alwaysShow = isDone && isLast;

    return (
        <div className="group">
            <div className="flex flex-col gap-4">
                {message.fragments.map((fragment) => {
                    switch (fragment.type) {
                        case "text":
                            return <FragmentText key={fragment.id} fragment={fragment} />;
                        case "action":
                            return <FragmentAction key={`${fragment.type}-${fragment.actionId}`} action={fragment} />;
                        case "fileModification":
                            return (
                                <FragmentFileModification
                                    key={`${fragment.type}-${fragment.actionId}`}
                                    fragment={fragment}
                                    defaultOpen={true}
                                />
                            );
                        case "environmentCreation":
                            return <FragmentEnvironmentCreation environmentId={fragment.environmentId} />;
                        case "thinking":
                            return <Thinking key={fragment.id} fragment={fragment} onClick={onClick} />;
                        case "hostAuthenticationRequired":
                            return <FragmentHostAuthentication key={fragment.id} fragment={fragment} />;
                        case "plan":
                            return <FragmentPlan key={fragment.id} fragment={fragment} />;
                    }
                })}
            </div>

            <div
                className="mt-2 flex min-h-[25px] justify-end duration-700 animate-in fade-in"
                aria-hidden={!alwaysShow}
            >
                <div
                    className={cn(
                        "flex gap-1 rounded-md border-0.5 border-border-base p-0.5 opacity-0 transition-opacity group-hover:opacity-100",
                        { "opacity-100": alwaysShow },
                    )}
                >
                    <Tooltip content="Give positive feedback">
                        <Button
                            variant="ghost"
                            className={cn("size-5 border-0", { "disabled:text-green-500": votedUp })}
                            size={"tiny"}
                            LeadingIcon={votedUp ? IconCheck : IconThumbsUp}
                            onClick={handleThumbsUp}
                            disabled={votedUp}
                            aria-label="Thumbs up"
                            tabIndex={alwaysShow ? undefined : -1}
                        />
                    </Tooltip>
                    <Tooltip content="Give negative feedback">
                        <Button
                            variant="ghost"
                            className={cn("size-5 border-0", { "disabled:text-green-500": votedDown })}
                            size={"tiny"}
                            LeadingIcon={votedDown ? IconCheck : IconThumbsDown}
                            onClick={handleThumbsDown}
                            disabled={votedDown}
                            aria-label="Thumbs down"
                            tabIndex={alwaysShow ? undefined : -1}
                        />
                    </Tooltip>
                    <Tooltip content="Copy to clipboard">
                        <Button
                            variant="ghost"
                            disabled={copied}
                            className={cn("size-5 border-0", { "disabled:text-green-500": copied })}
                            size={"tiny"}
                            LeadingIcon={copied ? IconCheck : IconCopy}
                            onClick={handleCopyToClipboard}
                            aria-label="Copy to clipboard"
                            tabIndex={alwaysShow ? undefined : -1}
                        />
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};

const ChatBubbleUser: FC<{ message: UserMessage }> = ({ message }) => {
    return (
        <div className="flex gap-2">
            <UserAvatar />
            <div>
                {message.fragments.map((fragment) => {
                    switch (fragment.type) {
                        case "text":
                            return <Markdown key={fragment.id} content={fragment.content} />;
                    }
                })}
            </div>
        </div>
    );
};
